/**
 * The staff notification summary, shared (TRUS-668).
 *
 * StaffNotificationsHost (mounted once in the signed-in staff shell) polls
 * GET /api/notifications/summary and raises the desktop notifications. The
 * bell only DISPLAYS what it fetched. Before, the bell did the polling itself,
 * and the bell is not rendered while the sidebar is collapsed: collapsing the
 * sidebar silently stopped every message notification.
 *
 * Which rows are new: every unread row with an id above the highest one already
 * seen (`latest_unread`, newest first, from the backend). The old rule, "the
 * unread count went up, show recent_unread[0]", missed messages two ways:
 * recent_unread is sorted by PRIORITY, so an older urgent / high row stayed in
 * front of every new (normal) message, and reading one row while another
 * arrived left the count unchanged.
 */
import { useSyncExternalStore } from 'react';

export interface StaffNotification {
  id: number;
  user_id: number;
  organization_id: number | null;
  notification_type: string;
  title: string;
  message: string;
  link: string | null;
  icon: string | null;
  priority: string;
  read: boolean;
  read_at: string | null;
  archived: boolean;
  archived_at: string | null;
  related_member_id: number | null;
  related_workflow_id: number | null;
  related_task_id: number | null;
  related_referral_id: number | null;
  related_enrollment_id: number | null;
  related_activity_id: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface StaffNotificationSummary {
  unread_count: number;
  read_count?: number | null;
  urgent_unread_count: number;
  total_count?: number | null;
  last_notification_at: string | null;
  recent_unread: StaffNotification[];
  /** Newest unread rows, id descending (backend TRUS-668; absent on an older API). */
  latest_unread?: StaffNotification[];
  latest_unread_id?: number | null;
}

/** At most this many notifications per poll; the rest stay in the bell. */
export const MAX_NOTIFICATIONS_PER_POLL = 3;

/** The highest unread id this summary knows about (0 when none). */
export function highestUnreadId(summary: StaffNotificationSummary | null | undefined): number {
  if (!summary) return 0;
  const rows = [...(summary.latest_unread ?? []), ...(summary.recent_unread ?? [])];
  return Math.max(0, Number(summary.latest_unread_id ?? 0) || 0, ...rows.map((r) => Number(r.id) || 0));
}

/** Unread rows newer than `lastSeenId`, newest first, at most MAX_NOTIFICATIONS_PER_POLL. */
export function newNotificationsSince(
  summary: StaffNotificationSummary | null | undefined,
  lastSeenId: number,
): StaffNotification[] {
  if (!summary) return [];
  // An older API has no latest_unread: recent_unread (priority order) still
  // finds a new row unless five more important ones are unread.
  const source = summary.latest_unread ?? summary.recent_unread ?? [];
  const seen = new Set<number>();
  return source
    .filter((r) => {
      const id = Number(r.id);
      if (!Number.isFinite(id) || id <= lastSeenId || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, MAX_NOTIFICATIONS_PER_POLL);
}

// --------------------------------------------------------------------------- //
// shared state
// --------------------------------------------------------------------------- //
let current: StaffNotificationSummary | null = null;
const listeners = new Set<() => void>();
let refresher: (() => Promise<void>) | null = null;

/**
 * What happened to each notification the host tried to show, by row id (the
 * Settings self-test waits on its row's entry):
 *  shown      raised on this computer
 *  duplicate  a push already showed it here (collapsed into that one)
 *  blocked    notification permission is not granted
 *  skipped    not raised on purpose (switched off, or the conversation is open)
 */
export type BellOutcome = 'shown' | 'duplicate' | 'blocked' | 'skipped';
const outcomes = new Map<number, BellOutcome>();
const outcomeWaiters = new Map<number, Array<(o: BellOutcome) => void>>();

export function setNotificationSummary(next: StaffNotificationSummary | null): void {
  current = next;
  listeners.forEach((l) => l());
}

export function getNotificationSummary(): StaffNotificationSummary | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The bell: re-renders whenever the host fetched a new summary. */
export function useNotificationSummary(): StaffNotificationSummary | null {
  return useSyncExternalStore(subscribe, getNotificationSummary, getNotificationSummary);
}

/** The host registers how to poll now; returns the unregister function. */
export function registerSummaryRefresher(fn: () => Promise<void>): () => void {
  refresher = fn;
  return () => {
    if (refresher === fn) refresher = null;
  };
}

/** Poll now (after a mark-read, or the self-test). No-op without a host. */
export async function refreshNotificationSummary(): Promise<void> {
  if (refresher) await refresher();
}

export function recordBellOutcome(id: number, outcome: BellOutcome): void {
  outcomes.set(id, outcome);
  if (outcomes.size > 200) {
    const oldest = outcomes.keys().next().value;
    if (oldest !== undefined) outcomes.delete(oldest);
  }
  const waiters = outcomeWaiters.get(id);
  if (waiters) {
    outcomeWaiters.delete(id);
    waiters.forEach((w) => w(outcome));
  }
}

/** Resolves with what the host did with row `id`, or null after `timeoutMs`. */
export function waitForBellOutcome(id: number, timeoutMs: number): Promise<BellOutcome | null> {
  const known = outcomes.get(id);
  if (known) return Promise.resolve(known);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      const list = outcomeWaiters.get(id) ?? [];
      outcomeWaiters.set(
        id,
        list.filter((w) => w !== done),
      );
      resolve(null);
    }, timeoutMs);
    const done = (o: BellOutcome) => {
      clearTimeout(timer);
      resolve(o);
    };
    outcomeWaiters.set(id, [...(outcomeWaiters.get(id) ?? []), done]);
  });
}

/** Tests only. */
export function __resetNotificationSummaryStore(): void {
  current = null;
  listeners.clear();
  refresher = null;
  outcomes.clear();
  outcomeWaiters.clear();
}
